import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Twirl as Hamburger } from "hamburger-react";
import { useModal } from "../../context/ModalContext";
import { logoLight, logoDark } from "../../assets";
import { menu } from "../../constants/index";
import MegaMenu from "./MegaMenu";
import useAppData from "../../hooks/useAppData";

export default function Header() {
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [isMobileView, setIsMobileView] = useState(false);
	const [showDropdown, setShowDropdown] = useState(false);
	const [isSticky, setIsSticky] = useState(false);

	const { categories } = useAppData();
	const { openQuote } = useModal();
	const location = useLocation();
	const timeoutRef = useRef(null);

	const forceLightLogoPaths = ["/", "/about-us", "/contact-us"];
	const shouldForceDark = forceLightLogoPaths.includes(location.pathname);

	const handleMouseEnter = () => {
		if (timeoutRef.current) clearTimeout(timeoutRef.current);
		setShowDropdown(true);
	};

	const handleMouseLeave = () => {
		timeoutRef.current = setTimeout(() => {
			setShowDropdown(false);
		}, 300);
	};

	useEffect(() => {
		const checkMobileView = () => setIsMobileView(window.innerWidth <= 992);
		const handleScroll = () => setIsSticky(window.scrollY > 50);

		checkMobileView();
		setIsMobileMenuOpen(false);

		window.addEventListener("resize", checkMobileView);
		window.addEventListener("scroll", handleScroll);

		return () => {
			window.removeEventListener("resize", checkMobileView);
			window.removeEventListener("scroll", handleScroll);
		};
	}, [location]);

	return (
		<nav
			className={`
				w-full z-50 transition-all duration-300
				${isSticky && shouldForceDark
					? "fixed top-0 bg-white shadow-md animate-slide-down" 
					: !shouldForceDark ? "bg-white": "absolute bg-transparent"}
			`}
		>
			<div className="max-w-7xl mx-auto flex justify-between items-center px-4 py-3">
				{/* LOGO */}
				<div>
					<Link to="/">
						<img
							src={isSticky || !shouldForceDark ? logoLight : logoDark}
							alt="logo"
							className="w-28 md:w-32"
						/>
					</Link>
				</div>

				{/* DESKTOP MENU */}
				{!isMobileView && (
					<div className="flex items-center gap-6">
						{/* NAV ITEMS */}
						<div className="flex gap-4">
							{menu.map((item, index) => {
								const isActive = location.pathname === item.path;

								return (
									<Link
										key={index}
										to={item.path}
										onMouseEnter={
											item.hasDropdown ? handleMouseEnter : undefined
										}
										onMouseLeave={
											item.hasDropdown ? handleMouseLeave : undefined
										}
										className={`
											px-4 py-2 rounded-md text-lg no-underline transition-colors relative flex items-center
											${
												isActive
													? "text-[#2abaf7]"
													: isSticky || !shouldForceDark
														? "text-black ha:text-[#2abaf7] ha:bg-slate-100"
														: "text-white ha:text-[#2abaf7] ha:bg-white/10"
											}
										`}
									>
										{item.name}

										{item.hasDropdown && (
											<svg
												className={`w-4 h-4 ml-1 transition-transform duration-200 ${
													showDropdown ? "rotate-180" : "rotate-0"
												}`}
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
											>
												<polyline points="6 9 12 15 18 9" />
											</svg>
										)}
									</Link>
								);
							})}
						</div>

						{/* BUTTON */}
						<button
							onClick={openQuote}
							className="bg-brand-500 ha:bg-brand-600 text-white px-4 py-3 rounded-md"
						>
							Request a quote
						</button>
					</div>
				)}

				{/* MOBILE */}
				{isMobileView && (
					<div className="flex items-center gap-3 bg-brand-500/20 p-1 rounded-lg">
						<Hamburger
							size={30}
							color={isSticky || !shouldForceDark ? "#606061" : "#fff"}
							toggled={isMobileMenuOpen}
							toggle={setIsMobileMenuOpen}
						/>
					</div>
				)}
			</div>

			{/* MOBILE MENU */}
			{isMobileView && isMobileMenuOpen && (
				<div
					className="fixed top-26.5 p-4 inset-0 bg-linear-to-b from-transparent to-black/80 z-40"
					onClick={() => setIsMobileMenuOpen(false)}
				>
					<div
						className="w-full p-2 rounded-lg bg-white flex flex-col gap-1 text-lg"
						onClick={(e) => e.stopPropagation()}
					>
						{menu.map((item, index) => {
							const isActive = location.pathname === item.path;
							return (
								<Link
									key={index}
									to={item.path}
									className={`p-3 rounded-lg 
									${isActive ? "text-brand-500 bg-black/10 font-bold" : "text-black ha:bg-black/10 ha:text-brand-500"}
								`}
								>
									{item.name}
								</Link>
							);
						})}
					</div>
				</div>
			)}

			{/* MEGA MENU */}
			{showDropdown && (
				<MegaMenu
					setShowDropdown={setShowDropdown}
					categories={categories}
					handleMouseEnter={handleMouseEnter}
					handleMouseLeave={handleMouseLeave}
				/>
			)}
		</nav>
	);
}
